import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import { useChat } from '../lib/chat';
import type { GuildDetail, GuildMemberRow } from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Panel, Tabs } from '../components/ui';
import { Banner } from './Guilds';
import { GuildProjects } from '../components/GuildProjects';
import { formatNumber, timeAgo } from '../lib/format';
import { TrainerAvatar } from '../components/avatar/TrainerAvatar';

export function GuildPage() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const { me, refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<GuildDetail>(() => api.get(`/guilds/${key}`), [key]);
  const [tab, setTab] = useState<'projects' | 'roster' | 'board'>('projects');
  const [busy, setBusy] = useState<string | null>(null);
  const chat = useChat();

  /** Opens the dock on this guild's room. */
  function openGuildChat(channelKey: string) {
    chat.selectChannel(channelKey);
    chat.setOpen(true);
  }

  async function act(path: string, body: unknown, message: string) {
    setBusy(path);
    try {
      await api.post(path, body);
      push('success', message);
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 420 }} />;

  const { guild, progression, you, members, posts, applications, seasonPoints, chatChannelKey } =
    state.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <Link className="tiny muted" to="/guilds">
            ← Guilds
          </Link>
          <h1 className="page-head__title">{guild.name}</h1>
          <div className="page-head__sub">{guild.motto || guild.description}</div>
        </div>
        {you.isMember ? (
          <Button
            variant="ghost"
            disabled={busy !== null}
            onClick={() =>
              void act(`/guilds/${key}/leave`, {}, `You left ${guild.name}.`).then(() =>
                navigate('/guilds'),
              )
            }
          >
            Leave guild
          </Button>
        ) : you.inAnotherGuild ? (
          <span className="tiny muted">You're already in a guild.</span>
        ) : guild.joinPolicy !== 'INVITE' ? (
          <Button
            variant="go"
            size="lg"
            disabled={busy !== null || guild.memberCount >= guild.memberSlots}
            onClick={() =>
              void act(
                `/guilds/${key}/join`,
                {},
                guild.joinPolicy === 'OPEN' ? 'Welcome in!' : 'Application sent.',
              )
            }
          >
            {guild.joinPolicy === 'OPEN' ? 'Join guild' : 'Apply to join'}
          </Button>
        ) : (
          <span className="tiny muted">Invitation only.</span>
        )}
      </div>

      <div className="pxframe pxframe--raised" style={{ overflow: 'hidden' }}>
        <Banner guild={guild} large />
        <div className="row row--between row--wrap" style={{ padding: 'var(--gap-sm) var(--gap)' }}>
          <div className="stack-sm" style={{ gap: 2, minWidth: 160 }}>
            <div className="field__label">Guild level {progression.level}</div>
            <Bar ratio={progression.ratio} variant="exp" />
            <div className="tiny muted">
              {progression.nextLevelExp
                ? `${formatNumber(progression.exp)} / ${formatNumber(progression.nextLevelExp)} EXP`
                : 'Fully grown'}
              {progression.unlocks && ` · ${progression.unlocks}`}
            </div>
          </div>
          <div className="tiny muted" style={{ textAlign: 'right' }}>
            {guild.memberCount} / {guild.memberSlots} members
            <br />
            {formatNumber(seasonPoints)} points given this season
          </div>
        </div>
      </div>

      {guild.description && <p className="muted">{guild.description}</p>}

      {you.isMember && applications.length > 0 && you.can.accept && (
        <Panel title="✉ Applications" hint={`${applications.length} waiting`}>
          <div className="stack-sm">
            {applications.map((row) => (
              <div key={row.id} className="pxwell row row--between row--wrap" style={{ padding: 8 }}>
                <div className="tiny">
                  <strong>{row.username}</strong>
                  {row.message && <> — "{row.message}"</>}
                </div>
                <div className="row" style={{ gap: 4 }}>
                  <Button
                    size="sm"
                    variant="go"
                    disabled={busy !== null}
                    onClick={() =>
                      void act(
                        `/guilds/${key}/applications/${row.id}`,
                        { action: 'accept' },
                        `${row.username} joined.`,
                      )
                    }
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy !== null}
                    onClick={() =>
                      void act(
                        `/guilds/${key}/applications/${row.id}`,
                        { action: 'decline' },
                        'Declined.',
                      )
                    }
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Guild chat is a channel in the dock now rather than a tab here, so a
          member can talk to their guild while doing anything else. This is the
          signpost to it. */}
      {you.isMember && chatChannelKey && (
        <div className="pxframe row row--between" style={{ padding: 'var(--gap-sm)' }}>
          <span className="tiny">
            🏰 Guild chat lives in the chat dock, bottom-right — talk while you play.
          </span>
          <Button size="sm" variant="go" onClick={() => openGuildChat(chatChannelKey!)}>
            Open guild chat
          </Button>
        </div>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'projects', label: 'Projects' },
          { value: 'roster', label: `Roster (${members.length})` },
          { value: 'board', label: `Board (${posts.length})` },
        ]}
      />

      {tab === 'projects' && <GuildProjects guildKey={key} />}

      {tab === 'roster' && (
        <Panel title="◈ Roster" hint={`${members.length} / ${guild.memberSlots}`}>
          <div>
            {members.map((member) => (
              <RosterRow
                key={member.userId}
                member={member}
                isMe={member.userId === me?.user.id}
                you={you}
                busy={busy !== null}
                onAct={(action, label) =>
                  void act(`/guilds/${key}/members/${member.userId}`, { action }, label)
                }
              />
            ))}
          </div>
        </Panel>
      )}

      {tab === 'board' && (
        <GuildBoard
          guildKey={key}
          posts={posts}
          canPost={you.isMember}
          canPin={you.can.pin}
          onChanged={() => state.reload()}
        />
      )}
    </div>
  );
}

/* --- Roster --------------------------------------------------------------- */

function RosterRow({
  member,
  isMe,
  you,
  busy,
  onAct,
}: {
  member: GuildMemberRow;
  isMe: boolean;
  you: GuildDetail['you'];
  busy: boolean;
  onAct: (action: string, label: string) => void;
}) {
  const rank = { LEADER: 0, OFFICER: 1, MEMBER: 2 };
  const outranks =
    you.role != null && rank[you.role] < rank[member.role] && !isMe;

  return (
    <div className={`roster-row ${isMe ? 'roster-row--me' : ''}`}>
      <span className={`role-chip role-chip--${member.role}`}>{member.role}</span>
      {/* Half the sprite's own size, which is an exact ratio — a roster row is
          a line of text tall, and a whole 80px trainer would set the row
          height for everything else on the page. */}
      <TrainerAvatar avatar={member.avatar} size={40} />
      {/* Their Lead Pokemon — slot 1 of their Team. Beside the trainer rather
          than in the text, so a roster reads as a group of trainers with
          Pokemon rather than as a table of usernames. Omitted entirely for an
          empty Team: a placeholder would read as a broken image. */}
      {member.lead && (
        <img
          className="pixel"
          src={member.lead.sprite}
          alt=""
          title={`${member.lead.name} · Lv ${member.lead.level}`}
          style={{ width: 32, height: 32, flex: 'none' }}
          loading="lazy"
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link to={`/trainer/${member.username}`} style={{ color: 'inherit' }}>
          <strong className="tiny">{member.username}</strong>
        </Link>
        <div className="tiny muted">
          Lv {member.trainerLevel} · {formatNumber(member.seasonPoints)} points this season · seen{' '}
          {timeAgo(member.lastSeenAt)}
          {member.isMuted && ' · muted'}
        </div>
      </div>
      {outranks && (
        <div className="row" style={{ gap: 4 }}>
          {you.can.promote && member.role === 'MEMBER' && (
            <Button size="sm" disabled={busy} onClick={() => onAct('promote', `${member.username} is an officer.`)}>
              Promote
            </Button>
          )}
          {you.can.promote && member.role === 'OFFICER' && (
            <Button size="sm" disabled={busy} onClick={() => onAct('demote', `${member.username} is a member.`)}>
              Demote
            </Button>
          )}
          {you.can.moderate && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                onAct(
                  member.isMuted ? 'unmute' : 'mute',
                  member.isMuted ? `${member.username} can talk again.` : `${member.username} is muted for an hour.`,
                )
              }
            >
              {member.isMuted ? 'Unmute' : 'Mute'}
            </Button>
          )}
          {you.can.kick && (
            <Button size="sm" disabled={busy} onClick={() => onAct('kick', `${member.username} was removed.`)}>
              Kick
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/* --- Bulletin board ------------------------------------------------------- */

function GuildBoard({
  guildKey,
  posts,
  canPost,
  canPin,
  onChanged,
}: {
  guildKey: string;
  posts: GuildDetail['posts'];
  canPost: boolean;
  canPin: boolean;
  onChanged: () => void;
}) {
  const { push } = useToast();
  const [body, setBody] = useState('');
  const [pin, setPin] = useState(false);
  const [busy, setBusy] = useState(false);

  async function post() {
    setBusy(true);
    try {
      await api.post(`/guilds/${guildKey}/posts`, { body, isPinned: pin });
      setBody('');
      setPin(false);
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not post that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="▤ Bulletin board" hint="the things that must still be here next week">
      <div className="stack">
        {canPost && (
          <div className="stack-sm">
            <textarea
              className="textarea"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={1000}
              placeholder="Raid on Saturday, bring water types…"
            />
            <div className="row row--between">
              {canPin ? (
                <label className="row tiny" style={{ gap: 6 }}>
                  <input type="checkbox" checked={pin} onChange={(event) => setPin(event.target.checked)} />
                  Pin it
                </label>
              ) : (
                <span />
              )}
              <Button size="sm" variant="primary" disabled={!body.trim() || busy} loading={busy} onClick={() => void post()}>
                Post
              </Button>
            </div>
          </div>
        )}

        {posts.length === 0 ? (
          <EmptyState art="▤" title="Nothing posted yet" />
        ) : (
          <div className="stack-sm">
            {posts.map((post) => (
              <div key={post.id} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
                <div className="row row--between tiny muted">
                  <span>
                    {post.isPinned && '📌 '}
                    {post.author}
                  </span>
                  <span>{timeAgo(post.createdAt)}</span>
                </div>
                <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{post.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
